import { useMemo, useState } from 'react';
import { HELP_DOCS, HELP_CATEGORIES, openPlaybook, visibleDocs } from '../config/helpDocs';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BookOpen, Download, Search, ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Help Center — cards grid of every playbook + resource. Client-side search
 * across title/description and a category chip filter. Zero backend needed;
 * PDFs live in /public/ and the config file registers each one.
 */
export default function HelpCenter() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const perms = useEffectivePermissions();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visibleDocs(HELP_DOCS, perms).filter(d => {
      if (category !== 'All' && d.category !== category) return false;
      if (!q) return true;
      return d.title.toLowerCase().includes(q) || d.description.toLowerCase().includes(q);
    });
  }, [query, category, perms]);

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="help-center">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-lg bg-blue-100 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-blue-900" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Help Center</h1>
        </div>
        <p className="text-slate-600 max-w-2xl">
          Playbooks, quick references, and workflow guides for TriCorps 360. Every document is a downloadable
          PDF you can print, email, or hand to a new team member. Can&apos;t find what you need?
          {' '}<a href="mailto:helpdesk@tricorps.com" className="text-blue-700 underline">Email the helpdesk</a>.
        </p>
      </div>

      {/* Search + category chips */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="help-search"
            placeholder="Search playbooks by title or topic…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {HELP_CATEGORIES.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              data-testid={`help-cat-${c.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                category === c
                  ? 'bg-brand text-brand-contrast border-brand'
                  : 'bg-surface text-slate-700 border-slate-200 hover:border-slate-400'
              )}
            >{c}</button>
          ))}
        </div>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="help-empty">
          No documents match your search. Try clearing the filter or search term.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="help-grid">
          {filtered.map(d => (
            <div
              key={d.id}
              data-testid={`help-card-${d.id}`}
              className="border border-slate-200 rounded-lg p-5 bg-surface hover:shadow-md transition-shadow flex flex-col"
            >
              <Badge className="self-start bg-slate-100 text-slate-700 hover:bg-slate-100 mb-2">{d.category}</Badge>
              <h3 className="font-semibold text-slate-900 text-base mb-1.5 leading-snug">{d.title}</h3>
              <p className="text-sm text-slate-600 flex-1 mb-4">{d.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Updated {d.updated}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" title="Open in new tab" data-testid={`help-open-${d.id}`}
                    onClick={() => openPlaybook(d)}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" title="Download PDF" data-testid={`help-download-${d.id}`}
                    onClick={() => openPlaybook(d, { download: true })}>
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
